import {
  AnchorButton,
  H6,
  Intent,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  TextArea,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { MultiSelect2 } from '@blueprintjs/select';
import classNames from 'classnames';
import * as React from 'react';
import { ItemSelector, Tooltip } from 'vet-bones/components';
import { alphaSort } from 'vet-bones/utils';

import {
  DataSourceTable,
  DataSourceTableSelectItem,
} from 'src/constants/dataSourceTable';
import {
  TEST_IDS,
  getProjectVirtualGraphPanelTableSelectItemTestId,
} from 'src/constants/testIds';
import { ProjectVirtualGraphsToCreate } from 'src/containers/ProjectResourceDialog/ProjectVirtualGraphCreationPanel/types';
import { useTableLabel } from 'src/containers/ProjectResourceSuggestionsDialog/hooks/useTableLabel';
import { getTablesFromVgsToCreate } from 'src/containers/ProjectVirtualGraphPanel/utils/getTablesFromVgsToCreate';
import { TableMetadataErrorResponse } from 'src/store/state/dataSources';
import * as copy from 'src/templates/copy';
import { stopPropagation } from 'src/utils/handlers/stopPropagation';
import { dataSourceTableToSelectItem } from 'src/utils/select/dataSourceTableToSelectItem';

enum ResourceScopeChoice {
  TABLE = 'TABLE',
  QUERY = 'QUERY',
}

type ProjectVirtualGraphPanelScopeProps = {
  customQueryText: string;
  disableEditing?: boolean;
  handleCustomQueryTextChange: (customQueryText: string) => void;
  handleIsUsingCustomQueryChange: (isUsingCustomQuery: boolean) => void;
  handleRemoveVirtualGraph?: (id: string) => void;
  handleSelectTable: (selectedTableItem: DataSourceTableSelectItem) => void;
  isForSingleTable?: boolean;
  isUsingCustomQuery: boolean;
  showError: boolean;
  tableMetadataErrorResponse: TableMetadataErrorResponse;
  tables: DataSourceTable[];
  validateCustomQuery: (activeQueryText: string) => void;
  virtualGraphsToCreate: ProjectVirtualGraphsToCreate;
};

export const ProjectVirtualGraphPanelScope: React.VFC<
  ProjectVirtualGraphPanelScopeProps
> = ({
  customQueryText,
  disableEditing,
  handleCustomQueryTextChange,
  handleRemoveVirtualGraph,
  handleSelectTable,
  isForSingleTable,
  isUsingCustomQuery,
  handleIsUsingCustomQueryChange,
  showError,
  tableMetadataErrorResponse,
  tables,
  validateCustomQuery,
  virtualGraphsToCreate,
}) => {
  const getTableLabel = useTableLabel(tables);
  const tableSelectItems = tables
    .map(dataSourceTableToSelectItem)
    .map<DataSourceTableSelectItem & { virtualGraphId?: string }>(
      (tableItem) => ({
        ...tableItem,
        text: getTableLabel(tableItem),
        virtualGraphId: Object.values(virtualGraphsToCreate).find(
          (virtualGraph) =>
            typeof virtualGraph.from === 'object' &&
            virtualGraph.from.name === tableItem.name &&
            virtualGraph.from.catalog === tableItem.catalog &&
            virtualGraph.from.schema === tableItem.schema
        )?.id,
      })
    )
    .sort(alphaSort((item) => item.text));

  let tooltipText = '';
  if (!disableEditing && tableMetadataErrorResponse) {
    tooltipText =
      tableMetadataErrorResponse.status === 403
        ? copy.components.projectResourceDialog.projectVirtualGraphPanel.form
            .tablesTooltip.noPermissions
        : tableMetadataErrorResponse.errorMessage;
  } else if (!disableEditing && tables.length === 0) {
    tooltipText =
      copy.components.projectResourceDialog.projectVirtualGraphPanel.form
        .tablesTooltip.noTables;
  }

  const selectedTableItems = getTablesFromVgsToCreate(
    virtualGraphsToCreate
  ).map((virtualGraph) => ({
    ...(virtualGraph.from as DataSourceTableSelectItem),
    virtualGraphId: virtualGraph.id,
  }));

  return (
    <div className="sd-project-virtual-graph-panel__resource-scope">
      <div>
        <H6>
          {
            copy.components.projectResourceDialog.projectVirtualGraphPanel.form
              .resourceScope
          }
        </H6>
        <Tooltip content={tooltipText} disabled={!tooltipText}>
          <div
            data-testid={
              TEST_IDS.projectVirtualGraphPanel.customQuery.radioGroup
            }
          >
            <RadioGroup
              className="sd-project-virtual-graph-panel__resource-scope__radio-group"
              onChange={({ currentTarget: { value: choice } }) => {
                handleIsUsingCustomQueryChange(
                  choice === ResourceScopeChoice.QUERY
                );
              }}
              selectedValue={
                isUsingCustomQuery
                  ? ResourceScopeChoice.QUERY
                  : ResourceScopeChoice.TABLE
              }
            >
              <Radio
                data-testid={
                  TEST_IDS.projectVirtualGraphPanel.customQuery.tableRadioButton
                }
                disabled={disableEditing || tableSelectItems.length === 0}
                label={copy.components.projectResourceDialog.projectVirtualGraphPanel.selectTable(
                  !isForSingleTable
                )}
                value={ResourceScopeChoice.TABLE}
              />
              <Radio
                data-testid={
                  TEST_IDS.projectVirtualGraphPanel.customQuery.queryRadioButton
                }
                disabled={disableEditing || tableSelectItems.length === 0}
                label={
                  copy.components.projectResourceDialog.projectVirtualGraphPanel
                    .customQuery
                }
                value={ResourceScopeChoice.QUERY}
              />
            </RadioGroup>
          </div>
        </Tooltip>
      </div>

      {isUsingCustomQuery ? (
        <div className="sd-project-virtual-graph-panel__resource-scope__custom-query">
          <TextArea
            data-testid={TEST_IDS.projectVirtualGraphPanel.customQuery.field}
            disabled={disableEditing}
            growVertically
            intent={showError ? Intent.DANGER : Intent.NONE}
            onChange={(evt: React.ChangeEvent<HTMLTextAreaElement>) => {
              handleCustomQueryTextChange(evt.target.value);
            }}
            placeholder={
              copy.components.projectResourceDialog.projectVirtualGraphPanel
                .form.pasteQuery
            }
            small
            value={customQueryText}
          />
          <AnchorButton
            data-testid={
              TEST_IDS.projectVirtualGraphPanel.customQuery.validationButton
            }
            disabled={disableEditing || customQueryText === ''}
            intent={Intent.PRIMARY}
            onClick={() => {
              validateCustomQuery(customQueryText);
            }}
          >
            {
              copy.components.projectResourceDialog.projectVirtualGraphPanel
                .form.runQuery
            }
          </AnchorButton>
        </div>
      ) : (
        <div
          className="sd-project-virtual-graph-panel__resource-scope__table-selector"
          data-testid={TEST_IDS.projectVirtualGraphPanel.tableSelect}
        >
          {isForSingleTable ? (
            <ItemSelector.Single
              buttonText={selectedTableItems[0]?.name || ''}
              items={tableSelectItems}
              itemTestId={getProjectVirtualGraphPanelTableSelectItemTestId}
              onButtonClick={stopPropagation}
              onClear={stopPropagation}
              onSelect={handleSelectTable}
              popoverProps={{
                backdropProps: { onClick: stopPropagation },
              }}
              selected={selectedTableItems[0] || null}
              testId={TEST_IDS.projectVirtualGraphPanel.singleTableInput}
            />
          ) : (
            <MultiSelect2<
              DataSourceTableSelectItem & { virtualGraphId?: string }
            >
              fill
              itemListRenderer={({ filteredItems, renderItem }) => {
                return filteredItems.length ? (
                  <Menu>{filteredItems.map(renderItem)}</Menu>
                ) : null;
              }}
              itemPredicate={(query, item) =>
                item.text.toLowerCase().includes(query.toLowerCase())
              }
              itemRenderer={(
                tableItem,
                {
                  handleClick,
                  modifiers: { active, disabled, matchesPredicate },
                }
              ) => {
                if (!matchesPredicate) {
                  return null;
                }
                return (
                  <MenuItem
                    key={tableItem.value}
                    active={active}
                    data-testid={getProjectVirtualGraphPanelTableSelectItemTestId(
                      tableItem.value
                    )}
                    disabled={disabled}
                    icon={
                      selectedTableItems.some(
                        (selectedTableItem) =>
                          selectedTableItem.value === tableItem.value
                      )
                        ? IconNames.TICK
                        : IconNames.BLANK
                    }
                    onClick={handleClick}
                    shouldDismissPopover={false}
                    text={tableItem.text}
                  />
                );
              }}
              items={tableSelectItems}
              onItemSelect={handleSelectTable}
              onRemove={(tableItemToRemove) => {
                if (tableItemToRemove.virtualGraphId) {
                  handleRemoveVirtualGraph?.(tableItemToRemove.virtualGraphId);
                }
              }}
              popoverProps={{
                rootBoundary: 'document',
                minimal: true,
                popoverClassName: classNames(
                  'sd-popover-menu',
                  'sd-table-selector-menu'
                ),
              }}
              resetOnSelect
              scrollToActiveItem={false}
              selectedItems={selectedTableItems}
              tagInputProps={{ disabled: disableEditing }}
              tagRenderer={({ text }) => text}
            />
          )}
        </div>
      )}
    </div>
  );
};
